import Backbone from 'backbone';
import uuid from '@bingads-webui-component-legacy/uuid/index';
import pageContext from '@bingads-webui-static-legacy/page-context';
import { schema } from '@bingads-webui-campaign/audience-campaign-shared';
import instrumentation from '@bingads-webui-campaign-legacy/instrumentation/index';
import { PerfMarkerFactory } from '@bingads-webui/performance-marker';
import parsedTextMaxLengthExtension from '@bingads-webui-campaign-legacy/crud-ad/viewmodel/adsEditor/parsedTextMaxLengthExtension';
import { loadResponsiveAdEditorAsync } from '@bingads-webui-campaign-legacy/audience-campaign/responsive-ad-editor-loader';

export default class View extends Backbone.View {
  /**
   * Initialize ad editor to create ad.
   *
   * @param {Object} options - options with data to initialize the editor.
   *
   * @param {Object} options.model - Backbone.Model with the model information of an existing ad,
   * used get the data to edit existing ad.
   *
   * @param {function} options.scope - gets the scope, used to get the campaign and adgroup ids.
   *
   * @return {Promise} - Promise of save request.
   */
  initialize(options = {}) {
    instrumentation.addScenario(schema);
    const logScenario = options.model ? instrumentation.aimAdEdit.create() : instrumentation.aimAdCreation.create();

    const initializeOptions = {
      logScenario,
      isGridDisplay: true,
      viewContext: {
        currentAdGroup: {
          get Id() {
            return options.scope().adGroupId;
          },
        },
        currentCampaign: {
          Id: options.scope().campaignId,
          CampaignType: options.scope().campaignType,
          CampaignSubType: options.scope().campaignSubType,
        },
      },
      isForVNext: options.isForVNext,
      isMultimediaAd: options.isMultimediaAd || false,
      isPlaceholderMMA: options.isPlaceholderMMA || false,
      isVideo: options.isVideo,
      isMobileUrlEnabled: options.isMobileUrlEnabled,
      isImpressionTrackingUrlsEnabled: options.isImpressionTrackingUrlsEnabled,
      newI18n: options.newI18n,
      serverContext: options.serverContext,
      pageContext,
      hasWebsiteImageOptedInObservable: options.hasWebsiteImageOptedInObservable,
      parsedTextMaxLengthExtension,
      isRecommendation: options.isRecommendation,
      campaignDomain: options.campaignDomain,
      edm: options.edm,
      jsdata: options.jsdata,
      isImproveMMA: options.isImproveMMA,
      biddingScheme: { Type: options.biddingStrategyType },
    };

    if (options.model) {
      initializeOptions.isUpdate = true;
      initializeOptions.ad = options.model;
    }

    if (options.perfMarker) {
      initializeOptions.perfMarker = options.perfMarker.createChild('CrudAudienceAdNew', { autoEnd: true });
    } else {
      initializeOptions.perfMarker = PerfMarkerFactory.createMarker('CrudAudienceAdNew', { autoEnd: true });
    }

    this.loadAdEditorPromise = loadResponsiveAdEditorAsync().then((ResponsiveAdEditor) => {
      this.audienceAd = new ResponsiveAdEditor(initializeOptions);
    });
  }

  remove() {
    if (this.audienceAd) {
      this.audienceAd.remove();
    }
    super.remove();
  }

  /**
   * Render the view.
   * @return {View} This object.
   */
  render() {
    this.loadAdEditorPromise.then(() => {
      this.$el.html(this.audienceAd.render().el);
    });

    return this;
  }

  /**
   * Saves ad.
   *
   * @param {function} updateGrid - function to be called in order to update the grid once the ad has been saved.
   *
   * @return {Promise} - Promise of save request.
   */
  saveAdAsync(updateGrid) {
    return this.loadAdEditorPromise.then(() => {
      return this.audienceAd.saveAdAsync().then((data) => {
        if (data) {
          return updateGrid(data, data.Id, 'Responsive');
        }
        return data;
      });
    });
  }

  /**
   * Saves ad and open up a new editor to create another.
   *
   * @param {function} updateGrid - function to be called in order to update the grid once the ad has been saved.
   *
   * @return {Promise} - Promise of save request.
   */
  saveAndCreateAnotherAsync(updateGrid) {
    return this.loadAdEditorPromise.then(() => {
      return this.audienceAd.saveAdAsync().then((data) => {
        if (data) {
          this.audienceAd.onCreateAnother();
          return updateGrid(data, data.Id, 'Responsive');
        }
        return data;
      });
    });
  }

  /**
   * Saves ad to client.
   *
   * @param {function} updateGrid - function to be called in order to update the grid once the ad has been saved.
   *
   * @return {Promise} - Promise of save request.
   */
  saveToClient(updateGrid) {
    return this.loadAdEditorPromise.then(() => {
      return this.audienceAd.saveToClient().then((data) => {
        if (data) {
          if (!data.Id) {
            data.Id = uuid.v4();
          }
          return updateGrid(data, data.Id, 'Responsive');
        }
        return data;
      });
    });
  }

  /**
   * Saves ad to client and open up a new editor to create another.
   *
   * @param {function} updateGrid - function to be called in order to update the grid once the ad has been saved.
   *
   * @return {Promise} - Promise of save request.
   */
  saveToClientAndCreateAnother(updateGrid) {
    return this.loadAdEditorPromise.then(() => {
      return this.audienceAd.saveToClient().then((data) => {
        if (data) {
          this.audienceAd.onCreateAnother();
          if (!data.Id) {
            data.Id = uuid.v4();
          }
          return updateGrid(data, data.Id, 'Responsive');
        }
        return data;
      });
    });
  }
}
